/**
 * Records: read-only views over the catalog and the history, for Arlene and her accountant.
 * Pure: they only read what's recorded and never change it. Every view is built to export.
 */
import { addDays, daysBetween, type CalendarDate } from "./calendar";
import type { Catalog, Pot } from "./catalog";
import type { Assessor } from "./assess";
import type { History, Placement, Rejection, WriteOff } from "./history";

/* ── Tax year (South Africa: 1 March to the last day of February) ─────────── */

export interface TaxYear {
  /** "2026/27" */
  readonly label: string;
  readonly from: CalendarDate;
  readonly to: CalendarDate;
}

export function taxYearOf(on: CalendarDate): TaxYear {
  const year = Number(on.slice(0, 4));
  const start = Number(on.slice(5, 7)) >= 3 ? year : year - 1;
  // The day before 1 March of the next year: 28 or 29 February.
  const to = addDays(`${start + 1}-03-01` as CalendarDate, -1);
  return { label: `${start}/${String((start + 1) % 100).padStart(2, "0")}`, from: `${start}-03-01` as CalendarDate, to };
}

const within = (on: CalendarDate, year: TaxYear) => on >= year.from && on <= year.to;

/* ── Asset register ───────────────────────────────────────────────────────── */

export type AssetStatus = "in-stock" | "placed" | "written-off" | "retired";

export interface AssetRow {
  readonly pot: Pot;
  readonly status: AssetStatus;
  readonly placedAt?: Placement;
  readonly writeOff?: WriteOff;
  readonly boughtThisYear: boolean;
  readonly writtenOffThisYear: boolean;
}

export interface AssetRegister {
  readonly year: TaxYear;
  readonly rows: readonly AssetRow[];
  readonly totals: {
    readonly pots: number;
    readonly inCirculation: number;
    readonly boughtThisYear: { readonly count: number; readonly costCents: number };
    readonly writtenOffThisYear: { readonly count: number; readonly costCents: number };
    /** Pots with no purchase cost recorded: the accountant needs these filled in. */
    readonly missingCost: number;
  };
}

const byNumber = new Intl.Collator("en", { numeric: true }).compare;

export function assetRegister(catalog: Catalog, history: History, year: TaxYear): AssetRegister {
  const rows: AssetRow[] = [...catalog.pots]
    .sort((a, b) => byNumber(a.id, b.id))
    .map((pot) => {
      const writeOff = history.writtenOff(pot.id);
      const placedAt = history.whereIs(pot.id);
      const status: AssetStatus = writeOff ? "written-off" : !pot.active ? "retired" : placedAt ? "placed" : "in-stock";
      return {
        pot,
        status,
        ...(placedAt ? { placedAt } : {}),
        ...(writeOff ? { writeOff } : {}),
        boughtThisYear: pot.asset?.boughtOn !== undefined && within(pot.asset.boughtOn, year),
        writtenOffThisYear: writeOff !== undefined && within(writeOff.on, year),
      };
    });
  const sum = (list: AssetRow[]) => list.reduce((n, r) => n + (r.pot.asset?.costCents ?? 0), 0);
  const bought = rows.filter((r) => r.boughtThisYear);
  const writtenOff = rows.filter((r) => r.writtenOffThisYear);
  return {
    year,
    rows,
    totals: {
      pots: rows.length,
      inCirculation: rows.filter((r) => r.status === "in-stock" || r.status === "placed").length,
      boughtThisYear: { count: bought.length, costCents: sum(bought) },
      writtenOffThisYear: { count: writtenOff.length, costCents: sum(writtenOff) },
      missingCost: rows.filter((r) => r.pot.asset?.costCents === undefined).length,
    },
  };
}

/* ── Client history and pot life story ───────────────────────────────────── */

export interface Stay extends Placement {
  /** Days the pot stood there (to today when it's still there). */
  readonly days: number;
}

const stay = (p: Placement, today: CalendarDate): Stay => ({ ...p, days: daysBetween(p.placedOn, p.collectedOn ?? today) });
const newestFirst = <T extends { placedOn?: CalendarDate; on?: CalendarDate }>(a: T, b: T) =>
  ((b.placedOn ?? b.on ?? "") > (a.placedOn ?? a.on ?? "") ? 1 : -1);

export interface ClientHistory {
  readonly clientId: string;
  readonly stays: readonly Stay[];
  readonly refusals: readonly Rejection[];
  readonly distinctPots: number;
}

export function clientHistory(history: History, clientId: string, today: CalendarDate): ClientHistory {
  const stays = history.placements.filter((p) => p.clientId === clientId).map((p) => stay(p, today)).sort(newestFirst);
  return {
    clientId,
    stays,
    refusals: history.rejections.filter((r) => r.clientId === clientId).sort(newestFirst),
    distinctPots: new Set(stays.map((s) => s.potId)).size,
  };
}

export interface PotStory {
  readonly potId: string;
  readonly stays: readonly Stay[];
  readonly refusals: readonly Rejection[];
  readonly writeOff?: WriteOff;
  readonly daysOut: number;
  readonly clients: number;
}

export function potStory(history: History, potId: string, today: CalendarDate): PotStory {
  const stays = history.placementsOf(potId).map((p) => stay(p, today)).sort(newestFirst);
  const writeOff = history.writtenOff(potId);
  return {
    potId,
    stays,
    refusals: history.rejections.filter((r) => r.potId === potId).sort(newestFirst),
    ...(writeOff ? { writeOff } : {}),
    daysOut: stays.reduce((n, s) => n + s.days, 0),
    clients: new Set(stays.map((s) => s.clientId)).size,
  };
}

/* ── Stock health ─────────────────────────────────────────────────────────── */

export interface Headroom {
  readonly clientId: string;
  /** Pots in circulation that may go to this client today (not red under the 12-month rule). */
  readonly eligible: number;
  /** Of those, how many also meet all the client's requirements (green somewhere at the client). */
  readonly goodFit: number;
  readonly inCirculation: number;
  /** The next blocked pot to become allowed again, if any. */
  readonly nextFreesUp?: { readonly potId: string; readonly on: CalendarDate };
}

/**
 * How much room each client has left under the 12-month promise: an early warning to buy stock
 * before the promise becomes impossible to keep. Fewest eligible first.
 */
export function headroom(catalog: Catalog, history: History, assessor: Assessor, on: CalendarDate): Headroom[] {
  const circulation = catalog.pots.filter((p) => p.active && !history.writtenOff(p.id));
  const clientIds = [...new Set(catalog.slots.map((s) => s.clientId))];
  return clientIds
    .map((clientId): Headroom => {
      const slots = catalog.slots.filter((s) => s.clientId === clientId);
      let eligible = 0;
      let goodFit = 0;
      let nextFreesUp: { potId: string; on: CalendarDate } | undefined;
      for (const pot of circulation) {
        const verdicts = slots.map((s) => assessor.assess(pot.id, s.id, on));
        const blocked = verdicts.find((v) => v.reasons.some((r) => r.code === "repeat-blocked"));
        if (blocked) {
          const reason = blocked.reasons.find((r) => r.code === "repeat-blocked");
          if (reason && reason.code === "repeat-blocked" && (!nextFreesUp || reason.allowedFrom < nextFreesUp.on)) {
            nextFreesUp = { potId: pot.id, on: reason.allowedFrom };
          }
          continue;
        }
        eligible += 1;
        // "recorded-elsewhere" is about today's whereabouts, not fit: ignore it here.
        const fits = verdicts.some((v) => v.reasons.every((r) => r.code === "recorded-elsewhere"));
        if (fits) goodFit += 1;
      }
      return { clientId, eligible, goodFit, inCirculation: circulation.length, ...(nextFreesUp ? { nextFreesUp } : {}) };
    })
    .sort((a, b) => a.eligible - b.eligible || a.goodFit - b.goodFit);
}

export interface IdlePot {
  readonly potId: string;
  /** When it last came home, or null when it has never been placed. */
  readonly since: CalendarDate | null;
  readonly days: number | null;
}

/** Pots sitting in stock for at least `minDays` (or never placed): candidates to use, sell or retire. */
export function idleStock(catalog: Catalog, history: History, on: CalendarDate, minDays = 60): IdlePot[] {
  return catalog.pots
    .filter((p) => p.active && !history.writtenOff(p.id) && !history.whereIs(p.id))
    .map((p): IdlePot => {
      const last = history.placementsOf(p.id).at(-1);
      const since = last?.collectedOn ?? null;
      return { potId: p.id, since, days: since ? daysBetween(since, on) : null };
    })
    .filter((i) => i.days === null || i.days >= minDays)
    .sort((a, b) => (b.days ?? Infinity) - (a.days ?? Infinity) || byNumber(a.potId, b.potId));
}

/* ── Monthly summary ──────────────────────────────────────────────────────── */

export interface MonthSummary {
  /** "2026-11" */
  readonly month: string;
  readonly placements: number;
  readonly clientsVisited: number;
  readonly overrides: readonly Placement[];
  readonly refusals: { readonly total: number; readonly byReason: Readonly<Record<string, number>> };
  readonly writeOffs: readonly WriteOff[];
}

export function monthSummary(history: History, month: string): MonthSummary {
  const inMonth = (on: CalendarDate) => on.startsWith(month);
  const placed = history.placements.filter((p) => inMonth(p.placedOn));
  const refusals = history.rejections.filter((r) => inMonth(r.on));
  const byReason: Record<string, number> = {};
  for (const r of refusals) byReason[r.reason || "No reason given"] = (byReason[r.reason || "No reason given"] ?? 0) + 1;
  return {
    month,
    placements: placed.length,
    clientsVisited: new Set(placed.map((p) => p.clientId)).size,
    overrides: placed.filter((p) => p.override),
    refusals: { total: refusals.length, byReason },
    writeOffs: history.writeOffs.filter((w) => inMonth(w.on)),
  };
}
